import React, { useState, useMemo } from "react";
import moment from "moment";
import throttle from "lodash/throttle";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const DATE_FORMAT = "MM/DD/YYYY";

export default function DateFilters({
  title = "Date Filters",
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) {
  const [openStartDate, setOpenStartDate] = useState(false);
  const [openEndDate, setOpenEndDate] = useState(false);

  const startDateValue = useMemo(() => {
    if (!startDate) return null;
    if (typeof startDate === "string") {
      return moment(startDate, DATE_FORMAT);
    }
    return startDate;
  }, [startDate]);

  const endDateValue = useMemo(() => {
    if (!endDate) return null;
    if (typeof endDate === "string") {
      return moment(endDate, DATE_FORMAT);
    }
    return endDate;
  }, [endDate]);

  const handleMonthScroll = useMemo(
    () =>
      throttle(
        event => {
          const delta = event.deltaY;
          if (delta === 0) return;

          const label = delta > 0 ? "Next month" : "Previous month";
          const button = event.currentTarget.querySelector(`button[aria-label="${label}"]`);

          if (button) {
            button.click();
          }
        },
        500,
        { trailing: false }
      ),
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box mt={2}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mr="3px">
              <DesktopDatePicker
                label="Start Date"
                format={DATE_FORMAT}
                open={openStartDate}
                onOpen={() => setOpenStartDate(true)}
                onClose={() => setOpenStartDate(false)}
                value={startDateValue}
                onChange={newValue => {
                  onStartDateChange(newValue);
                  if (newValue && newValue.isValid && newValue.isValid()) {
                    setOpenEndDate(true);
                  }
                }}
                slotProps={{
                  desktopPaper: { onWheel: handleMonthScroll },
                  textField: {
                    fullWidth: true,
                    size: "small",
                    autoComplete: "off",
                    onClick: () => setOpenStartDate(true),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DesktopDatePicker
              label="End Date"
              referenceDate={startDateValue}
              format={DATE_FORMAT}
              open={openEndDate}
              onOpen={() => setOpenEndDate(true)}
              onClose={() => setOpenEndDate(false)}
              value={endDateValue}
              onChange={onEndDateChange}
              slotProps={{
                desktopPaper: { onWheel: handleMonthScroll },
                textField: {
                  fullWidth: true,
                  size: "small",
                  autoComplete: "off",
                  onClick: () => setOpenEndDate(true),
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
